import { SETUP_GOALS } from "connected-components/optimize-experiments/constants"
import { percentageNoSymbolFormatter } from "helpers/formatters/numbers"
import { IsoDate } from "helpers/types/dates"
import {
  OptimizationConfiguration,
  OptimizationConstraints,
  OptimizationObjective,
} from "connected-components/optimize-experiments/hooks/types"
import { SetupSection, RulesSection } from "connected-components/optimize-experiments/configuration-sections"

interface GraphQLOptimizationConfiguration {
  objectives: Partial<OptimizationObjective>[]
  constraints: Partial<OptimizationConstraints>
}

function getObjectiveFromSetup(setup: SetupSection): OptimizationObjective {
  switch (setup.goal) {
    case SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK: {
      return { minimizeFactorRisk: true }
    }
    case SETUP_GOALS.TARGET_TOTAL_RISK: {
      if (!setup.targetTotalRiskValue) {
        throw new Error("Target total risk value is required")
      }
      return { targetTotalRisk: setup.targetTotalRiskValue / 100 }
    }
    case SETUP_GOALS.MIN_VOLATILITY: {
      return { minimizeTotalRisk: true }
    }
    default: {
      const _exhaustive: never = setup.goal
      throw new Error(`Unknown goal: ${_exhaustive}`)
    }
  }
}

function getSetupFromObjective(objective: Partial<OptimizationObjective>): SetupSection {
  if (objective.targetTotalRisk !== undefined) {
    return {
      goal: SETUP_GOALS.TARGET_TOTAL_RISK,
      targetTotalRiskValue: parseFloat((objective.targetTotalRisk * 100).toFixed(2)),
    }
  }

  if (objective.minimizeTotalRisk !== undefined) {
    return {
      goal: SETUP_GOALS.MIN_VOLATILITY,
    }
  }

  if (objective.minimizeFactorRisk !== undefined) {
    return {
      goal: SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK,
    }
  }

  throw new Error(`Invalid objective: ${JSON.stringify(objective)}`)
}

interface FormSections {
  setup: SetupSection
  rules: RulesSection
}

export function transformFormToConfiguration(
  endDate: IsoDate,
  setup: SetupSection,
  rules: Partial<RulesSection> = {},
): OptimizationConfiguration {
  const objectives: OptimizationObjective[] = [getObjectiveFromSetup(setup)]

  const constraints: Partial<OptimizationConstraints> = {}
  if (rules?.maxTurnover !== undefined && rules.maxTurnover !== null) {
    const maxTurnoverValue = parseFloat(String(rules.maxTurnover))
    constraints.maxTurnover = isNaN(maxTurnoverValue) ? 1.0 : maxTurnoverValue / 100.0
  }

  return {
    on: endDate,
    objectives,
    constraints,
  }
}

export function transformConfigurationToForm(
  configuration: Partial<GraphQLOptimizationConfiguration>,
): FormSections {
  const objectives = configuration.objectives

  if (!objectives || objectives.length === 0) {
    throw new Error("No objectives found in configuration")
  }

  const objective = objectives[0]
  const setup = getSetupFromObjective(objective)

  const rules: RulesSection = {
    maxTurnover: configuration.constraints?.maxTurnover !== undefined
      ? percentageNoSymbolFormatter(configuration.constraints.maxTurnover)
      : "100.00",
  }

  return {
    setup,
    rules,
  }
}
